import React from 'react';
import { Link } from 'react-router-dom';

import { usePaperDocument } from '../hooks/usePaperDocument';
import './paper.css';

const REPO = 'https://github.com/TheoTime01/Spok--Boston-Dynamics-Spot-inspired-robot';
const VIDEO = 'https://youtu.be/bJ2OreaSHS8';
const EMBED = 'https://www.youtube-nocookie.com/embed/bJ2OreaSHS8';

const DESCRIPTION =
  'SPOK: a 12-DoF, ~200 EUR quadruped built on ROS 2 Humble, micro-ROS and CHAMP. ' +
  'Kinematics, servo calibration, state estimation and navigation parameters of the platform.';

/* Display equations are written as MathML so that they render natively in the
 * document font instead of pulling in a formula-typesetting library. React
 * renders MathML tags, but going through innerHTML keeps the markup verbatim
 * and keeps this file readable. */
const Equation = ({ tag, math }) => (
  <div className="eq">
    <div className="eq-body" dangerouslySetInnerHTML={{ __html: math }} />
    <div className="eq-tag">({tag})</div>
  </div>
);

const EQ_ABDUCTION = `
<math display="block">
  <msub><mi>&#x3B8;</mi><mn>1</mn></msub><mo>=</mo><mo>&#x2212;</mo>
  <mrow><mo stretchy="true">[</mo>
    <mi>arctan</mi><mo>&#x2061;</mo><mspace width="0.15em"/>
    <mfrac><mi>y</mi><mi>z</mi></mfrac>
    <mo>&#x2212;</mo>
    <mrow><mo stretchy="true">(</mo>
      <mfrac><mi>&#x3C0;</mi><mn>2</mn></mfrac>
      <mo>&#x2212;</mo>
      <mi>arccos</mi><mo>&#x2061;</mo><mspace width="0.15em"/>
      <mrow><mo stretchy="true">(</mo>
        <mo>&#x2212;</mo>
        <mfrac>
          <msub><mi>l</mi><mn>0</mn></msub>
          <msqrt><mrow><msup><mi>y</mi><mn>2</mn></msup><mo>+</mo><msup><mi>z</mi><mn>2</mn></msup></mrow></msqrt>
        </mfrac>
      <mo stretchy="true">)</mo></mrow>
    <mo stretchy="true">)</mo></mrow>
  <mo stretchy="true">]</mo></mrow>
</math>`;

const EQ_REACH = `
<math display="block">
  <msqrt><mrow>
    <msup><mrow><mi>x</mi><mo>&#x2032;</mo></mrow><mn>2</mn></msup>
    <mo>+</mo>
    <msup><mrow><mi>z</mi><mo>&#x2032;</mo></mrow><mn>2</mn></msup>
  </mrow></msqrt>
  <mo>&lt;</mo>
  <mrow><mo>|</mo><msub><mi>l</mi><mn>1</mn></msub><mo>|</mo></mrow>
  <mo>+</mo>
  <mrow><mo>|</mo><msub><mi>l</mi><mn>2</mn></msub><mo>|</mo></mrow>
</math>`;

const EQ_KNEE = `
<math display="block">
  <msub><mi>&#x3B8;</mi><mn>3</mn></msub><mo>=</mo>
  <mi>k</mi><mo>&#x22C5;</mo>
  <mi>arccos</mi><mo>&#x2061;</mo><mspace width="0.15em"/>
  <mrow><mo stretchy="true">(</mo>
    <mfrac>
      <mrow>
        <msup><mrow><mi>x</mi><mo>&#x2032;</mo></mrow><mn>2</mn></msup><mo>+</mo>
        <msup><mrow><mi>z</mi><mo>&#x2032;</mo></mrow><mn>2</mn></msup><mo>&#x2212;</mo>
        <msubsup><mi>l</mi><mn>1</mn><mn>2</mn></msubsup><mo>&#x2212;</mo>
        <msubsup><mi>l</mi><mn>2</mn><mn>2</mn></msubsup>
      </mrow>
      <mrow>
        <mn>2</mn><msub><mi>l</mi><mn>1</mn></msub><msub><mi>l</mi><mn>2</mn></msub>
      </mrow>
    </mfrac>
  <mo stretchy="true">)</mo></mrow>
  <mo>,</mo><mspace width="1em"/>
  <mi>k</mi><mo>=</mo><mo>&#xB1;</mo><mn>1</mn>
</math>`;

const EQ_THIGH = `
<math display="block">
  <msub><mi>&#x3B8;</mi><mn>2</mn></msub><mo>=</mo>
  <mi>arctan</mi><mo>&#x2061;</mo><mspace width="0.15em"/>
  <mfrac><mrow><mi>x</mi><mo>&#x2032;</mo></mrow><mrow><mi>z</mi><mo>&#x2032;</mo></mrow></mfrac>
  <mo>&#x2212;</mo>
  <mi>arctan</mi><mo>&#x2061;</mo><mspace width="0.15em"/>
  <mrow><mo stretchy="true">(</mo>
    <mfrac>
      <mrow><msub><mi>l</mi><mn>2</mn></msub><mi>sin</mi><mo>&#x2061;</mo><mspace width="0.15em"/><msub><mi>&#x3B8;</mi><mn>3</mn></msub></mrow>
      <mrow><msub><mi>l</mi><mn>1</mn></msub><mo>+</mo><msub><mi>l</mi><mn>2</mn></msub><mi>cos</mi><mo>&#x2061;</mo><mspace width="0.15em"/><msub><mi>&#x3B8;</mi><mn>3</mn></msub></mrow>
    </mfrac>
  <mo stretchy="true">)</mo></mrow>
</math>`;

const EQ_SERVO = `
<math display="block">
  <mi>&#x3C4;</mi><mo stretchy="true">(</mo><mi>&#x3B8;</mi><mo stretchy="true">)</mo><mo>=</mo>
  <msub><mi>&#x3C4;</mi><mi>min</mi></msub><mo>+</mo>
  <mfrac>
    <mrow><msub><mi>&#x3C4;</mi><mi>max</mi></msub><mo>&#x2212;</mo><msub><mi>&#x3C4;</mi><mi>min</mi></msub></mrow>
    <mrow><msub><mi>&#x3B8;</mi><mi>max</mi></msub><mo>&#x2212;</mo><msub><mi>&#x3B8;</mi><mi>min</mi></msub></mrow>
  </mfrac>
  <mrow><mo stretchy="true">(</mo><mi>&#x3B8;</mi><mo>&#x2212;</mo><msub><mi>&#x3B8;</mi><mi>min</mi></msub><mo stretchy="true">)</mo></mrow>
</math>`;

const EQ_QUANT = `
<math display="block">
  <mi>&#x394;</mi><mi>&#x3B8;</mi><mo>=</mo>
  <mfrac>
    <mrow><mi>&#x394;</mi><mi>&#x3C4;</mi></mrow>
    <mrow><mo>|</mo><mi>d</mi><mi>&#x3C4;</mi><mo>/</mo><mi>d</mi><mi>&#x3B8;</mi><mo>|</mo></mrow>
  </mfrac>
  <mo>,</mo><mspace width="1em"/>
  <mi>&#x394;</mi><mi>&#x3C4;</mi><mo>=</mo>
  <mfrac><mi>T</mi><msup><mn>2</mn><mn>12</mn></msup></mfrac>
  <mo>=</mo><mn>4.883</mn><mspace width="0.2em"/><mi>&#x3BC;</mi><mi>s</mi>
</math>`;

const EQ_KF_PREDICT = `
<math display="block">
  <mtable columnalign="left">
    <mtr><mtd>
      <msup><mover><mi>&#x3C6;</mi><mo>^</mo></mover><mo>&#x2212;</mo></msup>
      <mo>=</mo>
      <mover><mi>&#x3C6;</mi><mo>^</mo></mover>
      <mo>+</mo>
      <mi>&#x394;</mi><mi>t</mi>
      <mrow><mo stretchy="true">(</mo><msub><mi>&#x3C9;</mi><mi>m</mi></msub><mo>&#x2212;</mo><mover><mi>b</mi><mo>^</mo></mover><mo stretchy="true">)</mo></mrow>
    </mtd></mtr>
    <mtr><mtd>
      <msup><mi>P</mi><mo>&#x2212;</mo></msup><mo>=</mo>
      <mi>F</mi><mi>P</mi><msup><mi>F</mi><mo>&#x22A4;</mo></msup><mo>+</mo><mi>Q</mi>
      <mo>,</mo><mspace width="1em"/>
      <mi>F</mi><mo>=</mo>
      <mrow><mo stretchy="true">[</mo>
        <mtable>
          <mtr><mtd><mn>1</mn></mtd><mtd><mo>&#x2212;</mo><mi>&#x394;</mi><mi>t</mi></mtd></mtr>
          <mtr><mtd><mn>0</mn></mtd><mtd><mn>1</mn></mtd></mtr>
        </mtable>
      <mo stretchy="true">]</mo></mrow>
    </mtd></mtr>
  </mtable>
</math>`;

const EQ_KF_UPDATE = `
<math display="block">
  <mtable columnalign="left">
    <mtr><mtd>
      <mi>S</mi><mo>=</mo><msubsup><mi>P</mi><mn>11</mn><mo>&#x2212;</mo></msubsup><mo>+</mo><mi>R</mi>
      <mo>,</mo><mspace width="1em"/>
      <mi>K</mi><mo>=</mo>
      <mfrac><mn>1</mn><mi>S</mi></mfrac>
      <msup>
        <mrow><mo stretchy="true">[</mo><msubsup><mi>P</mi><mn>11</mn><mo>&#x2212;</mo></msubsup><mo>,</mo><msubsup><mi>P</mi><mn>21</mn><mo>&#x2212;</mo></msubsup><mo stretchy="true">]</mo></mrow>
        <mo>&#x22A4;</mo>
      </msup>
    </mtd></mtr>
    <mtr><mtd>
      <mrow><mo stretchy="true">[</mo><mover><mi>&#x3C6;</mi><mo>^</mo></mover><mo>,</mo><mover><mi>b</mi><mo>^</mo></mover><mo stretchy="true">]</mo></mrow>
      <mo>&#x2190;</mo>
      <mrow><mo stretchy="true">[</mo><msup><mover><mi>&#x3C6;</mi><mo>^</mo></mover><mo>&#x2212;</mo></msup><mo>,</mo><msup><mover><mi>b</mi><mo>^</mo></mover><mo>&#x2212;</mo></msup><mo stretchy="true">]</mo></mrow>
      <mo>+</mo>
      <msup><mi>K</mi><mo>&#x22A4;</mo></msup>
      <mrow><mo stretchy="true">(</mo><msub><mi>&#x3C6;</mi><mi>acc</mi></msub><mo>&#x2212;</mo><msup><mover><mi>&#x3C6;</mi><mo>^</mo></mover><mo>&#x2212;</mo></msup><mo stretchy="true">)</mo></mrow>
    </mtd></mtr>
  </mtable>
</math>`;

const EQ_TOF = `
<math display="block">
  <mi>d</mi><mo>=</mo>
  <mfrac><mrow><mi>c</mi><mspace width="0.15em"/><mi>&#x394;</mi><mi>t</mi></mrow><mn>2</mn></mfrac>
  <mo>,</mo><mspace width="1em"/>
  <mi>c</mi><mo>=</mo><mn>343</mn><mspace width="0.2em"/><mi>m</mi><mo>/</mo><mi>s</mi>
</math>`;

const SpokProject = () => {
  const { theme, toggleTheme } = usePaperDocument(
    'SPOK: a low-cost ROS 2 quadruped | Théotime Perrichet'
  );

  return (
    <article className="paper-doc">
      <div className="doc-bar">
        <Link to="/">&larr; Théotime Perrichet</Link>
        <button type="button" onClick={toggleTheme}>
          {theme === 'dark' ? ': Light :' : ': Dark :'}
        </button>
      </div>

      <h1>
        SPOK: a &euro;200 quadruped for reconnaissance in spaces people should
        not enter
      </h1>

      <p className="author">Théotime Perrichet &nbsp;&middot;&nbsp; Arnaud Sibenaler</p>
      <p className="doc-affiliation">Final-year engineering project, M.Eng. Robotics, CPE Lyon</p>
      <p className="doc-dateline">Lyon, France · 2024–2025</p>

      <ul className="doc-links">
        <li><a href={REPO}>Source code</a></li>
        <li><a href={VIDEO}>Pitch video</a></li>
      </ul>

      <div className="abstract">
        <h2>Abstract</h2>
        <p>
          SPOK is a twelve-degree-of-freedom quadruped assembled from printed PLA
          parts and hobby servos for a bill of materials of roughly &euro;200,
          built to walk into fire-damaged structures ahead of the people who
          would otherwise have to. Its control stack is split across three
          compute tiers: an RP2040 microcontroller running micro-ROS for
          microsecond-accurate ranging, a Raspberry&nbsp;Pi Zero&nbsp;2&nbsp;W
          driving twelve servos over I²C, and an off-board laptop running the
          gait generator and the Nav2 stack, all federated by ROS&nbsp;2 Humble
          over a single DDS domain. Teleoperated locomotion, body-pose control,
          kinematic odometry, ranging and video telemetry were validated on the
          physical robot; autonomous navigation was validated in Gazebo only.
          This write-up records the kinematic model, the actuator calibration,
          the estimator and planner parameters the system actually runs with,
          and argues that the remaining simulation-to-hardware gap is dominated
          by exteroception, two ultrasonic beams against a simulated planar
          scanner, rather than by anything in the control chain.
        </p>
        <p className="keywords">
          <strong>Keywords:</strong> legged locomotion · inverse kinematics ·
          ROS 2 · micro-ROS · Nav2 · sensor fusion · low-cost robotics
        </p>
      </div>

      <nav aria-label="Table of contents">
        <ol>
          <li><a href="#s1">Introduction</a></li>
          <li><a href="#s2">Platform</a></li>
          <li><a href="#s3">Software architecture</a></li>
          <li><a href="#s4">Leg kinematics and actuation</a></li>
          <li><a href="#s5">Gait generation and body control</a></li>
          <li><a href="#s6">State estimation</a></li>
          <li><a href="#s7">Autonomous navigation</a></li>
          <li><a href="#s8">Perception and telemetry</a></li>
          <li><a href="#s9">Results</a></li>
          <li><a href="#s10">Limitations and further work</a></li>
          <li><a href="#s11">Reproducing the system</a></li>
        </ol>
      </nav>

      <figure>
        <img src="/images/spok2.jpg" alt="The assembled SPOK quadruped standing on a workbench" />
        <figcaption>
          The assembled platform. Twelve MG996R servos, printed PLA structure,
          a Raspberry Pi Zero 2 W under the top cover and a USB webcam at the
          front.
        </figcaption>
      </figure>

      <h2 id="s1">1. Introduction</h2>
      <p>
        Structure fires are fought partly blind. Before a crew commits to an
        interior attack, someone has to know what is behind the door: whether
        the floor still carries load, where the fire has travelled, whether
        anyone is inside. Wheeled and tracked robots answer some of that, but
        collapsed ceilings, stairs and debris fields are exactly the terrain
        where continuous ground contact stops being a reasonable assumption.
        Legged platforms handle it, and commercial quadrupeds of that class are
        priced in the tens of thousands of euros, far outside what a municipal
        fire service will put into a consumable reconnaissance asset.
      </p>
      <p>
        The question this project asks is narrow and empirical: with a
        two-order-of-magnitude cut in budget, which of those capabilities
        survive? The design constraints were fixed up front: a bill of
        materials near &euro;200, printed structural parts, hobby-grade servos
        with no joint feedback beyond their internal loop, and no laser
        scanner. Everything else was allowed to follow.
      </p>
      <p>
        SPOK builds on the SpotMicroAI chassis{' '}
        <a href="#r1">[1]</a>, with the printed geometry taken from the
        published model <a href="#r2">[2]</a> and modified to fit our
        electronics; gait generation and inverse kinematics come from CHAMP{' '}
        <a href="#r3">[3]</a>, running on ROS 2 Humble. Our contribution is the
        integration: a three-tier compute split, the actuator calibration that
        makes open-loop servos usable for locomotion, the estimator
        configuration, and an honest account of which capabilities crossed from
        simulation to hardware and which did not.
      </p>

      <figure>
        <div className="video-frame">
          <iframe
            src={EMBED}
            title="SPOK quadruped robot: project pitch video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </div>
        <figcaption>
          Project pitch video: the platform walking under joystick control, the
          operator video feed, and the navigation stack running in simulation.
          If the embed does not load, the video is on{' '}
          <a href={VIDEO}>YouTube</a>.
        </figcaption>
      </figure>

      <h2 id="s2">2. Platform</h2>

      <h3>2.1 Kinematic structure</h3>
      <p>
        Four legs, three revolute joints each: an abduction joint rotating about
        the body&rsquo;s longitudinal axis, then hip and knee joints rotating
        about the lateral axis, so each leg has one out-of-plane and two
        in-plane degrees of freedom. Table 1 lists the geometry as it is
        declared in the URDF model, which is also the geometry the inverse
        kinematics of &sect;4 is solved against.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Leg and body geometry, read from the URDF model.</caption>
          <thead>
            <tr>
              <th scope="col">Quantity</th>
              <th scope="col">Symbol</th>
              <th scope="col" className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Hip offset from body centre (longitudinal, lateral)</td><td>n/a</td><td className="num">0.093, 0.036 m</td></tr>
            <tr><td>Abduction axis to hip axis, lateral</td><td><em>l</em><sub>0</sub></td><td className="num">0.052 m</td></tr>
            <tr><td>Thigh length</td><td><em>l</em><sub>1</sub></td><td className="num">0.1204 m</td></tr>
            <tr><td>Shank length</td><td><em>l</em><sub>2</sub></td><td className="num">0.115 m</td></tr>
            <tr><td>Maximum leg extension</td><td><em>l</em><sub>1</sub>&nbsp;+&nbsp;<em>l</em><sub>2</sub></td><td className="num">0.2354 m</td></tr>
            <tr><td>Nominal stance height</td><td><em>h</em></td><td className="num">0.150 m</td></tr>
            <tr><td>Joint velocity limit</td><td>n/a</td><td className="num">1.5 rad/s</td></tr>
            <tr><td>Modelled mass</td><td><em>m</em></td><td className="num">5.03 kg</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        The nominal stance sits at 64&nbsp;% of maximum extension. That is a
        deliberate operating point: it leaves the leg room to extend into a
        drop and to fold under a step-up, and it keeps the knee far from the
        singular fully-extended configuration where the Jacobian degenerates
        and the inverse kinematics of Eq.&nbsp;(3) loses conditioning.
      </p>

      <h3>2.2 Electronics</h3>
      <p>
        Two processors, one I²C bus, one serial link. The Raspberry Pi
        Zero 2 W runs Ubuntu 22.04 and ROS 2 Humble and owns the I²C bus,
        which carries both the PCA9685 servo driver (address 0x40) and the
        MPU-6050 inertial unit (address 0x68). The RP2040 runs bare-metal
        micro-ROS and reaches the Pi over USB CDC serial at 115&nbsp;200 baud,
        bridged into the ROS graph by the micro-ROS agent{' '}
        <a href="#r4">[4]</a> running in a container on the Pi.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Bill of materials. Total approximately &euro;200.</caption>
          <thead>
            <tr>
              <th scope="col">Component</th>
              <th scope="col">Qty</th>
              <th scope="col">Role</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Raspberry Pi Zero 2 W</td><td className="num">1</td><td>On-board ROS 2 host: servo driver, IMU, watchdog, camera</td></tr>
            <tr><td>Raspberry Pi Pico (RP2040)</td><td className="num">1</td><td>micro-ROS node: ultrasonic timing, status LEDs</td></tr>
            <tr><td>PCA9685 servo driver HAT</td><td className="num">1</td><td>16-channel, 12-bit PWM at 50 Hz over I²C</td></tr>
            <tr><td>MG996R servo</td><td className="num">12</td><td>Joint actuation, internal position loop</td></tr>
            <tr><td>MPU-6050</td><td className="num">1</td><td>3-axis accelerometer and gyroscope</td></tr>
            <tr><td>HC-SR04</td><td className="num">2</td><td>Forward ultrasonic ranging</td></tr>
            <tr><td>USB webcam</td><td className="num">1</td><td>Operator video feed</td></tr>
            <tr><td>Red LED + 220 &#8486;</td><td className="num">4</td><td>Link-state indication</td></tr>
            <tr><td>7.5 V supply</td><td className="num">1</td><td>Servo and logic power</td></tr>
            <tr><td>Printed PLA parts, bearings, fasteners</td><td className="num">n/a</td><td>Structure; 8 bearings, anti-slip feet</td></tr>
          </tbody>
        </table>
      </div>

      <figure>
        <img
          className="fig-plate"
          src="/images/spok-schema.png"
          alt="Wiring schematic of the SPOK electronics: Raspberry Pi Zero 2 W, PCA9685 servo driver, MPU-6050, Raspberry Pi Pico, ultrasonic sensors and LEDs"
        />
        <figcaption>
          Electrical architecture. The I²C bus is shared between the servo
          driver and the IMU; the Pico hangs off USB serial and owns everything
          that needs deterministic microsecond timing.
        </figcaption>
      </figure>

      <h2 id="s3">3. Software architecture</h2>
      <p>
        The stack is split across three tiers, and the split is a consequence of
        what each tier can guarantee. The RP2040 gets the ultrasonic sensors
        because time-of-flight measurement needs microsecond edge timing that a
        preemptible Linux userspace process cannot promise. The Pi Zero 2 W gets
        everything that must be physically close to the hardware: the I²C
        transactions, the camera, the link watchdog. The laptop gets the gait
        generator, the extended Kalman filters and Nav2, because none of those
        fit in the Pi Zero&rsquo;s compute budget alongside the rest.
      </p>
      <p>
        All three tiers join the same DDS domain
        (<code>ROS_DOMAIN_ID=0</code>), so the placement of a node is a
        deployment decision rather than a code change: the same{' '}
        <code>quadruped_controller_node</code> that runs on the laptop would
        run unmodified on-board given the compute. That property is what makes
        the &ldquo;move everything on-board&rdquo; item in &sect;10 a hardware
        upgrade rather than a rewrite.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Principal nodes, their host and their update rate.</caption>
          <thead>
            <tr>
              <th scope="col">Node</th>
              <th scope="col">Host</th>
              <th scope="col">Rate</th>
              <th scope="col">Interface</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><code>pico_node</code></td><td>RP2040</td><td>10 Hz</td><td>pub range &times; 2; sub link-state edge</td></tr>
            <tr><td><code>joint_servo_controller</code></td><td>Pi Zero 2 W</td><td>on message</td><td>sub <code>/…/joint_trajectory</code></td></tr>
            <tr><td><code>mpu6050_node</code></td><td>Pi Zero 2 W</td><td>10 Hz</td><td>pub <code>/imu/data</code></td></tr>
            <tr><td><code>gyro_node</code></td><td>Pi Zero 2 W</td><td>200 Hz</td><td>pub <code>/robot_orientation</code></td></tr>
            <tr><td><code>connection_node</code></td><td>Pi Zero 2 W</td><td>2 Hz</td><td>pub <code>/connection_state</code></td></tr>
            <tr><td><code>usb_cam</code></td><td>Pi Zero 2 W</td><td>camera rate</td><td>pub <code>/image_raw</code></td></tr>
            <tr><td><code>joy_teleop_node</code></td><td>Laptop</td><td>on message</td><td>pub <code>/cmd_vel</code>, <code>/body_pose</code></td></tr>
            <tr><td><code>quadruped_controller_node</code></td><td>Laptop</td><td>control loop</td><td>pub joint trajectory</td></tr>
            <tr><td>EKF pair (<code>robot_localization</code>)</td><td>Laptop</td><td>50 Hz</td><td>pub <code>/odom</code>, TF</td></tr>
            <tr><td>Nav2 controller / planner</td><td>Laptop</td><td>20 Hz</td><td>pub <code>/cmd_vel</code></td></tr>
            <tr><td><code>face_detection_node</code></td><td>Laptop</td><td>every 10th frame</td><td>pub <code>/output_video</code></td></tr>
          </tbody>
        </table>
      </div>

      <p>
        The cost of the split is that the Wi-Fi link sits inside the control
        path. &sect;9 shows that this, and not compute, is what bounded the
        perception pipeline.
      </p>

      <h2 id="s4">4. Leg kinematics and actuation</h2>

      <h3>4.1 Closed-form inverse kinematics</h3>
      <p className="definition">
        <em>Leg frame.</em> For a single leg, let the origin sit at the
        abduction axis, with <em>x</em> forward, <em>y</em> lateral and{' '}
        <em>z</em> downward-negative, and let{' '}
        <strong>p</strong> = (<em>x</em>, <em>y</em>, <em>z</em>) be the
        commanded foot position expressed in that frame. Let{' '}
        <em>l</em><sub>0</sub> be the lateral offset from the abduction axis to
        the hip axis, and <em>l</em><sub>1</sub>, <em>l</em><sub>2</sub> the
        thigh and shank lengths of Table 1.
      </p>
      <p>
        The gait generator produces foot positions; the actuators take joint
        angles. Because the leg is a three-link chain with two parallel axes,
        the map inverts in closed form, with no iteration and no Jacobian, which
        is what lets all twelve joints be solved every control tick on modest
        hardware.
      </p>
      <p>
        The abduction angle is fixed first, since it is the only joint that
        moves the foot out of the sagittal plane. It follows from the projection
        of <strong>p</strong> onto the frontal plane:
      </p>
      <Equation tag="1" math={EQ_ABDUCTION} />
      <p>
        With <em>&theta;</em><sub>1</sub> known, the target is rotated back into
        the leg&rsquo;s sagittal plane and translated to the hip axis,{' '}
        <strong>p</strong>&prime; = <em>T</em>(&minus;<em>x</em><sub>u</sub>,
        0, &minus;<em>z</em><sub>u</sub>)&nbsp;<em>R</em><sub>x</sub>
        (&minus;<em>&theta;</em><sub>1</sub>)&nbsp;<strong>p</strong>, which
        reduces the remaining problem to a planar two-link chain. That chain has
        a solution only inside its annular workspace:
      </p>
      <Equation tag="2" math={EQ_REACH} />
      <p>
        The implementation tests Eq.&nbsp;(2) before solving and returns the
        previous joint angles when it fails, so an unreachable command degrades
        into a hold rather than into a <code>NaN</code> propagating to twelve
        servos. Inside the workspace, the knee angle comes from the law of
        cosines and the hip angle from the resulting triangle:
      </p>
      <Equation tag="3" math={EQ_KNEE} />
      <Equation tag="4" math={EQ_THIGH} />
      <p>
        The sign <em>k</em> in Eq.&nbsp;(3) selects between the two mirror
        solutions of the law of cosines, knee forward or knee back. It is
        pinned by configuration rather than chosen at runtime
        (<code>knee_orientation: &quot;&lt;&lt;&quot;</code>), so all four knees
        flex the same way and the robot cannot flip branch mid-stride. Two
        constant corrections then absorb the offset between the URDF link frames
        and the joint axes: <em>&theta;</em><sub>2</sub> &larr;{' '}
        <em>&theta;</em><sub>2</sub> + <em>&alpha;</em> and{' '}
        <em>&theta;</em><sub>3</sub> &larr; <em>&theta;</em><sub>3</sub> +{' '}
        <em>&beta;</em> &minus; <em>&alpha;</em>, with{' '}
        <em>&alpha;</em> = arccos(<em>x</em><sub>s</sub>/<em>l</em><sub>1</sub>)
        &minus; &pi;/2 and <em>&beta;</em> = arccos(<em>x</em><sub>f</sub>/
        <em>l</em><sub>2</sub>) &minus; &pi;/2.
      </p>

      <h3>4.2 Joint angles to pulse widths</h3>
      <p>
        The MG996R is an open-loop position device from the controller&rsquo;s
        point of view: it accepts a pulse width and closes its own loop around
        it, reporting nothing back. There is therefore no analytical mapping
        from joint angle to command, because the relation depends on horn spline
        indexing, linkage geometry and how the servo was seated during assembly,
        and has to be measured per joint. Each of the eight in-plane joints was
        driven to two reference poses and the pulse width recorded (Table 4),
        giving a two-point affine fit:
      </p>
      <Equation tag="5" math={EQ_SERVO} />

      <div className="scroll-wrapper">
        <table>
          <caption>
            Measured two-point calibration for the front legs; the hind legs
            follow the same pattern. Sign differences encode the left–right
            mirroring of the servo horns.
          </caption>
          <thead>
            <tr>
              <th scope="col">Joint</th>
              <th scope="col" className="num"><em>&theta;</em><sub>min</sub>, <em>&tau;</em><sub>min</sub></th>
              <th scope="col" className="num"><em>&theta;</em><sub>max</sub>, <em>&tau;</em><sub>max</sub></th>
              <th scope="col" className="num">d<em>&tau;</em>/d<em>&theta;</em></th>
            </tr>
          </thead>
          <tbody>
            <tr><td>front left hip</td><td className="num">&minus;1.2360 rad, 2490 &micro;s</td><td className="num">&minus;0.9356 rad, 2360 &micro;s</td><td className="num">&minus;433 &micro;s/rad</td></tr>
            <tr><td>front right hip</td><td className="num">&minus;1.2360 rad, 620 &micro;s</td><td className="num">&minus;0.9356 rad, 790 &micro;s</td><td className="num">+566 &micro;s/rad</td></tr>
            <tr><td>front left knee</td><td className="num">2.5123 rad, 410 &micro;s</td><td className="num">1.8437 rad, 890 &micro;s</td><td className="num">&minus;718 &micro;s/rad</td></tr>
            <tr><td>front right knee</td><td className="num">2.5123 rad, 2730 &micro;s</td><td className="num">1.8437 rad, 2260 &micro;s</td><td className="num">+703 &micro;s/rad</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        The calibration angles are identical across all four legs, which is the
        signature of the procedure: the robot was placed once in a known pose
        and every servo trimmed to it, rather than each joint being swept
        independently. The abduction joints are handled separately, as a
        proportional trim around their neutral pulse, because their travel
        during locomotion is small.
      </p>
      <p>
        The calibration also fixes the command resolution. The PCA9685 divides
        a <em>T</em> = 20 ms frame into 4096 steps, so a single count is
        4.883&nbsp;&micro;s, and the resulting angular quantisation is that
        divided by the local slope of Eq.&nbsp;(5):
      </p>
      <Equation tag="6" math={EQ_QUANT} />
      <p>
        At the hip&rsquo;s 433&nbsp;&micro;s/rad this is 11.3&nbsp;mrad, about
        0.65&deg;, which at full leg extension is a foot displacement near
        2.7&nbsp;mm. Set against the 30&nbsp;mm swing height of &sect;5, command
        quantisation is roughly an order of magnitude below the motion it has to
        resolve, so it is not the limiting error term: gear backlash in the
        MG996R and compliance in the printed PLA structure both exceed it.{' '}
        <a href="#f1" id="f1-ref"><sup>1</sup></a>
      </p>
      <p>
        One further consequence of open-loop actuation: the PID gains that
        appear in the controller configuration
        (<em>p</em> = 100, <em>i</em> = 0.2, <em>d</em> = 1.0) are exercised only
        by the simulated effort interface in Gazebo. On hardware, nothing
        closes a loop around joint position outside the servos themselves.
      </p>

      <h2 id="s5">5. Gait generation and body control</h2>
      <p>
        Locomotion uses CHAMP&rsquo;s phase-based generator <a href="#r3">[3]</a>:
        a phase signal per leg divides the cycle into stance and swing, foot
        trajectories are generated in the body frame from the commanded body
        velocity, and &sect;4 converts them to joint angles. The parameters that
        define SPOK&rsquo;s gait are given in Table 5.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Gait configuration.</caption>
          <thead>
            <tr><th scope="col">Parameter</th><th scope="col" className="num">Value</th><th scope="col">Note</th></tr>
          </thead>
          <tbody>
            <tr><td>Nominal body height</td><td className="num">0.150 m</td><td>64 % of maximum extension</td></tr>
            <tr><td>Swing height</td><td className="num">0.030 m</td><td>Foot clearance during swing</td></tr>
            <tr><td>Stance depth</td><td className="num">0.000 m</td><td>Flat stance trajectory</td></tr>
            <tr><td>Stance duration</td><td className="num">0.25 s</td><td>Sets the cycle period</td></tr>
            <tr><td>Max linear velocity <em>v<sub>x</sub></em></td><td className="num">0.30 m/s</td><td></td></tr>
            <tr><td>Max linear velocity <em>v<sub>y</sub></em></td><td className="num">0.50 m/s</td><td>Lateral crab walk</td></tr>
            <tr><td>Max angular velocity <em>&omega;<sub>z</sub></em></td><td className="num">1.00 rad/s</td><td></td></tr>
            <tr><td>Knee orientation</td><td className="num">&laquo;&nbsp;&lt;&lt;&nbsp;&raquo;</td><td>Branch selection for Eq. (3)</td></tr>
            <tr><td>Odometry scale factor</td><td className="num">0.9</td><td>Empirical slip correction, §6.2</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        The teleoperation path is: joystick &rarr; <code>/cmd_vel</code> &rarr;
        velocity smoother &rarr; <code>/cmd_vel/smooth</code> &rarr; gait
        generator &rarr; inverse kinematics &rarr; joint trajectory &rarr; PWM.
        The smoother caps acceleration at 1.0&nbsp;m/s&sup2; and
        2.0&nbsp;rad/s&sup2;, which matters more here than on a wheeled robot:
        a velocity step applied to a phase-based gait generator asks the swing
        legs for a displacement they cannot reach inside one cycle, and the
        result is a stumble rather than a fast start. The joystick&rsquo;s own
        defaults (0.2&nbsp;m/s, 0.4&nbsp;rad/s) sit below the gait maxima, so
        normal operation stays inside the envelope with margin.
      </p>
      <p>
        The same controller also accepts a body pose independent of the feet,
        which is how a quadruped looks around without walking. The joystick maps
        the right stick and shoulder buttons to roll &plusmn;0.349&nbsp;rad
        (20&deg;), pitch &plusmn;0.175&nbsp;rad (10&deg;), yaw
        &plusmn;0.436&nbsp;rad (25&deg;) and a body-height offset. Pose commands
        enter Eq.&nbsp;(1)–(4) as a rigid transform on the foot targets, so the
        feet stay planted while the body moves over them, which is useful
        for aiming the camera into a doorway from outside it.
      </p>

      <h2 id="s6">6. State estimation</h2>

      <h3>6.1 Attitude from the inertial unit</h3>
      <p>
        The accelerometer measures a gravity-referenced attitude that is
        unbiased but noisy under the accelerations of walking; the gyroscope
        measures a rate that integrates smoothly but drifts. The standard
        resolution is a two-state Kalman filter per axis, tracking the angle{' '}
        <em>&phi;</em> and the gyro bias <em>b</em> jointly, so that the
        accelerometer disciplines the drift the gyro accumulates:
      </p>
      <Equation tag="7" math={EQ_KF_PREDICT} />
      <Equation tag="8" math={EQ_KF_UPDATE} />
      <p>
        with <em>Q<sub>&phi;</sub></em> = 0.001,{' '}
        <em>Q<sub>b</sub></em> = 0.003 and <em>R</em> = 0.03, running on a 5 ms
        timer. The accelerometer measurement is{' '}
        <em>&phi;</em><sub>acc</sub> = atan2(<em>a<sub>y</sub></em>,{' '}
        <em>a<sub>z</sub></em>) for roll and atan2(&minus;<em>a<sub>x</sub></em>,{' '}
        <em>a<sub>z</sub></em>) for pitch. A second node publishes the raw
        inertial data as a <code>sensor_msgs/Imu</code> at 10 Hz for the
        odometry filters below.
      </p>
      <p>
        <em>Remark.</em> The ratio <em>R</em>/<em>Q<sub>&phi;</sub></em> = 30 tells you the tuning
        intent: the filter is set to trust the integrated rate over the
        accelerometer between updates, which is the right choice on a walking
        robot where every footfall injects a spurious acceleration transient.
      </p>

      <h3>6.2 Odometry</h3>
      <p>
        Position comes from the legs. During stance a foot is assumed to be a
        non-slipping anchor, so the body displacement between two ticks can be
        read off the joint angles of the stance legs through forward kinematics,
        with the gait generator&rsquo;s contact signal deciding which legs
        count. Two extended Kalman filters
        (<code>robot_localization</code> <a href="#r5">[5]</a>) then run at
        50 Hz over that estimate:
      </p>
      <ul>
        <li>
          a full 6-DoF filter fusing the kinematic body pose with inertial
          linear velocities, producing the <code>base_link</code> &rarr;{' '}
          <code>base_footprint</code> transform, which is what keeps the
          footprint level while the body pitches;
        </li>
        <li>
          a planar filter fusing the resulting velocity estimate with inertial
          yaw and yaw rate, producing the <code>odom</code> &rarr;{' '}
          <code>base_footprint</code> transform that Nav2 consumes.
        </li>
      </ul>
      <p>
        The non-slip assumption is where this estimator is weakest, and the
        configuration says so: an <code>odom_scaler</code> of 0.9 sits in the
        gait configuration, meaning kinematic odometry over-reports travel by
        about 11&nbsp;% and is corrected by a constant. That is an honest
        calibration for a fixed surface and a fixed gait, and not a model: on a
        different floor the coefficient is different, and nothing in the system
        measures it. Bounding this properly needs an exteroceptive reference,
        which is the same missing sensor as &sect;9.
      </p>

      <h2 id="s7">7. Autonomous navigation</h2>
      <p>
        Autonomy is delegated to Nav2 <a href="#r6">[6]</a>, configured as in
        Table 6. The architectural point is that Nav2&rsquo;s output is{' '}
        <code>/cmd_vel</code>, the same interface the joystick drives. Autonomy
        is therefore a drop-in replacement for the operator, and everything
        below that topic is shared between the two modes and validated by both.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Navigation stack configuration.</caption>
          <thead>
            <tr><th scope="col">Parameter</th><th scope="col" className="num">Value</th></tr>
          </thead>
          <tbody>
            <tr><td>Costmap resolution</td><td className="num">0.05 m</td></tr>
            <tr><td>Robot radius (inflation)</td><td className="num">0.22 m</td></tr>
            <tr><td>Global planner</td><td className="num">NavFn, 20 Hz</td></tr>
            <tr><td>Local controller</td><td className="num">DWB, 20 Hz</td></tr>
            <tr><td>Max <em>v<sub>x</sub></em> / <em>&omega;<sub>z</sub></em></td><td className="num">0.26 m/s / 1.0 rad/s</td></tr>
            <tr><td>Path smoother</td><td className="num">SimpleSmoother</td></tr>
            <tr><td>Recovery behaviours</td><td className="num">Spin, BackUp, DriveOnHeading, Wait</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        Two of those numbers are chosen rather than inherited. The inflation
        radius of 0.22&nbsp;m circumscribes the standing footprint including the
        legs, which extend well beyond the 0.186&nbsp;m hip-to-hip body, and a
        planner that reasons about the body alone will clip doorframes with the
        knees. The planner&rsquo;s 0.26&nbsp;m/s ceiling sits 13&nbsp;% under the
        gait generator&rsquo;s 0.30&nbsp;m/s, leaving the smoother headroom to
        act without saturating the gait.
      </p>
      <p>
        In simulation the costmap is fed by a simulated planar scanner,
        republished onto <code>/scan</code>. On the physical robot there is no
        scanner; &sect;9 takes up what follows from that.
      </p>

      <h2 id="s8">8. Perception and telemetry</h2>

      <h3>8.1 Ultrasonic ranging</h3>
      <p>
        Two HC-SR04 sensors are driven from the RP2040. Each measurement pulses
        the trigger for 10&nbsp;&micro;s and times the echo, giving range by
        time of flight:
      </p>
      <Equation tag="9" math={EQ_TOF} />
      <p>
        Both sensors are read on a 10 Hz timer. The implementation busy-waits on
        the echo line, and with the HC-SR04&rsquo;s no-echo timeout near
        38&nbsp;ms, two sequential measurements can occupy about 76&nbsp;ms of
        the 100&nbsp;ms period in the worst case, leaving roughly 24&nbsp;ms of
        slack.
        That is a defensible budget on a bare-metal microcontroller with one
        executor and nothing else to do; it is precisely the budget that would
        be unpredictable under the Pi&rsquo;s scheduler, which is why the sensor
        lives here.
      </p>

      <h3>8.2 Video</h3>
      <p>
        A USB webcam publishes through <code>usb_cam</code>. A detection node
        runs a Viola–Jones cascade over every tenth frame, chosen over a CNN
        detector on the explicit constraint that it must run on a CPU this size.
        The result is reported in &sect;9: the bottleneck turned out not to be
        the classifier. A second node overlays the current ultrasonic range on
        the video, so the operator sees distance and image together rather than
        having to fuse two windows mentally; that one was kept.
      </p>

      <h3>8.3 Link watchdog</h3>
      <p>
        A node on the Pi pings the operator&rsquo;s laptop twice a second and
        publishes the result continuously, plus an edge-triggered message to the
        Pico whenever the state changes. The Pico switches its LED blink pattern
        on that edge. This is a small feature with a specific justification: the
        moment the link drops is exactly the moment the operator loses video, so
        the indication has to be out-of-band and visible on the robot itself, to
        someone standing where the robot is rather than where the laptop is.
      </p>

      <h2 id="s9">9. Results</h2>
      <p>
        Table 7 records which capabilities were exercised in simulation, on
        hardware, or both.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Capability matrix.</caption>
          <thead>
            <tr>
              <th scope="col">Capability</th>
              <th scope="col" className="num">Gazebo</th>
              <th scope="col" className="num">Hardware</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Teleoperated walking</td><td className="num">yes</td><td className="num">yes</td></tr>
            <tr><td>Body pose control (roll, pitch, yaw, height)</td><td className="num">yes</td><td className="num">yes</td></tr>
            <tr><td>Kinematic and inertial odometry</td><td className="num">yes</td><td className="num">yes</td></tr>
            <tr><td>Obstacle ranging</td><td className="num">simulated scanner</td><td className="num">2 beams</td></tr>
            <tr><td>Video feedback</td><td className="num">n/a</td><td className="num">yes</td></tr>
            <tr><td>Link-loss indication</td><td className="num">n/a</td><td className="num">yes</td></tr>
            <tr><td>Autonomous navigation</td><td className="num">yes</td><td className="num">no</td></tr>
            <tr><td>Person detection</td><td className="num">yes</td><td className="num">no</td></tr>
          </tbody>
        </table>
      </div>

      <p>
        The two negatives have different causes, and separating them is the main
        engineering result of the project.
      </p>
      <p>
        <strong>Person detection failed on bandwidth, not compute.</strong> The
        cascade was selected to fit the available processing, and it does. What
        it does not survive is the frame rate that actually arrives over the
        radio link: sampling one frame in ten of a stream that is already
        starved leaves the detector with too little to work on to be useful. The
        fix is not a better detector. It is either moving the detector on-board,
        next to the camera, or not sampling.
      </p>
      <p>
        <strong>Autonomy failed on exteroception, not control.</strong> Nav2
        builds an occupancy costmap, and a costmap needs a dense angular sample
        of the surroundings. A planar scanner at one-degree resolution supplies
        360 measurements per revolution; SPOK&rsquo;s hardware supplies two
        forward beams at 10&nbsp;Hz. The costmap layer has, effectively, no
        input. Nothing below <code>/cmd_vel</code> differs between the simulated
        and the physical robot: the gait generator, the inverse kinematics of
        &sect;4 and the estimator of &sect;6 are the same code, exercised daily
        by teleoperation on hardware. The gap between the two columns of Table 7
        is one sensor.
      </p>
      <p>
        That is a more useful outcome than a working demonstration would have
        been, because it is falsifiable and it prices the next step: the
        difference between a teleoperated platform and an autonomous one, on
        this robot, is a planar LiDAR and its mounting.
      </p>

      <h2 id="s10">10. Limitations and further work</h2>
      <ol>
        <li>
          <strong>Replace the ultrasonic pair with a planar LiDAR.</strong> This
          addresses &sect;9 directly, and it also bounds the odometry drift of
          &sect;6.2, since scan matching supplies the exteroceptive reference
          that the constant slip factor currently stands in for. One sensor,
          two problems.
        </li>
        <li>
          <strong>Move the gait generator on-board.</strong> Taking the radio
          link out of the control path removes the failure mode the watchdog of
          &sect;8.3 exists to announce. It needs more compute than a
          Pi Zero 2 W, but no code change (&sect;3).
        </li>
        <li>
          <strong>Higher-torque actuators.</strong> At the modelled 5&nbsp;kg,
          the MG996R has little margin, and its gear backlash, rather than command
          quantisation (&sect;4.2), dominates foot placement error.
        </li>
        <li>
          <strong>Reconcile the gyroscope scale factor.</strong> The inertial
          unit is configured for its widest rate range while the driver converts
          counts with the sensitivity constant of the narrowest, so the rate
          term entering Eq.&nbsp;(7) is scaled well below its true value and the
          filter leans almost entirely on the accelerometer. This is consistent
          with the self-balancing loop having been left out of the delivered
          system, and it should be fixed before that loop is revisited.
        </li>
        <li>
          <strong>Add a manipulator.</strong> A light arm, as on the platform
          that inspired this one, would move the robot from reconnaissance to
          intervention: opening a door is a different mission from looking
          through one.
        </li>
      </ol>

      <h2 id="s11">11. Reproducing the system</h2>
      <p>
        The workspace splits by target, since the microcontroller package cannot
        be built on the laptop and the navigation stack should not be built on
        the Pi:
      </p>
      <pre><code>{`# laptop
colcon build --packages-ignore micro_ros_raspberrypicosdk

# raspberry pi
colcon build --packages-select spok_rob micro_ros_raspberrypicosdk`}</code></pre>
      <p>Bring-up, on the robot and then on the laptop:</p>
      <pre><code>{`# robot: servo, imu and watchdog nodes
ros2 launch spok_rob spok.launch.py

# robot: bridge the micro-ROS node on the Pico into the graph
docker run -it --rm -v /dev:/dev --privileged --net=host \\
  microros/micro-ros-agent:humble serial --dev /dev/ttyACM0 -b 115200

# laptop: teleoperation
ros2 launch quadruped_robot spot_bringup.launch.py

# laptop: autonomous mode (simulation)
ros2 launch quadruped_robot spot_bringup_nav.launch.py \\
  headless:=False params_file:="nav2_params_empty.yaml" map:="<map>.yaml"`}</code></pre>
      <p>
        Both machines run Ubuntu 22.04 with ROS 2 Humble on{' '}
        <code>ROS_DOMAIN_ID=0</code>.
      </p>

      <h2>Acknowledgements</h2>
      <p>
        SPOK stands on the SpotMicroAI community&rsquo;s documentation and on
        CHAMP, whose gait generator and kinematics library saved this project
        the several months it would have taken to write from scratch. The
        printed geometry derives from a published model, modified for our
        electronics.
      </p>

      <h2>References</h2>
      <ol>
        <li id="r1">
          SpotMicroAI documentation.{' '}
          <a href="https://spotmicroai.readthedocs.io/en/latest/">
            spotmicroai.readthedocs.io
          </a>
        </li>
        <li id="r2">
          Printed chassis model.{' '}
          <a href="https://www.thingiverse.com/thing:3638679">
            thingiverse.com/thing:3638679
          </a>
        </li>
        <li id="r3">
          CHAMP, quadruped controller for ROS.{' '}
          <a href="https://github.com/chvmp/champ">github.com/chvmp/champ</a>
        </li>
        <li id="r4">
          micro-ROS. <a href="https://micro.ros.org/">micro.ros.org</a>
        </li>
        <li id="r5">
          T. Moore and D. Stouch, <em>robot_localization</em>.{' '}
          <a href="http://docs.ros.org/en/melodic/api/robot_localization/html/index.html">
            documentation
          </a>
        </li>
        <li id="r6">
          S. Macenski et al., <em>The Marathon 2: A Navigation System</em>,
          IROS 2020. <a href="https://navigation.ros.org/">navigation.ros.org</a>
        </li>
        <li id="r7">
          Project source.{' '}
          <a href={REPO}>
            github.com/TheoTime01/Spok--Boston-Dynamics-Spot-inspired-robot
          </a>
        </li>
      </ol>

      <div className="footnotes">
        <p id="f1">
          <a href="#f1-ref">1.</a> The MG996R specifies no backlash figure;
          the claim here is an ordering argument, not a measurement. Quantifying
          it needs joint-level encoders the platform does not have.
        </p>
      </div>

      <p className="colophon">
        Every parameter quoted above is read from the configuration and source
        files in the project repository <a href="#r7">[7]</a> rather than
        re-derived: gait and navigation values from the YAML configuration, link
        geometry from the URDF model, servo calibration from the on-robot
        controller. No new experiments were run for this write-up. Typeset with{' '}
        <a href="https://github.com/vincentdoerig/latex-css">LaTeX.css</a>.
      </p>
    </article>
  );
};

export default SpokProject;
